import moment from 'moment';
import { FC, useCallback } from 'react';
import RdDateRangePicker from 'react-dates/lib/components/DateRangePicker';
import DateRangePickerShape from 'react-dates/lib/shapes/DateRangePickerShape';
import isInclusivelyAfterDay from 'react-dates/lib/utils/isInclusivelyAfterDay';
import isInclusivelyBeforeDay from 'react-dates/lib/utils/isInclusivelyBeforeDay';
import registerCSSInterfaceWithDefaultTheme from 'react-dates/lib/utils/registerCSSInterfaceWithDefaultTheme';
import { FormItem, IFormItemProps } from '../Form/FormItem';
import { Icon } from '../Icon/Icon';
import { StyledDatePicker } from './styles';

registerCSSInterfaceWithDefaultTheme();

export interface IDatePickerProps {
  /** If enabled only allows past dates to be selected. (Default: false) */
  isPastDatesOnly?: boolean;
  /** If enabled only allows future dates to be selected. (Default: false) */
  isFutureDatesOnly?: boolean;
  /** Sets the unit of the date limit. e.g. year, month, week, day. (Default: year) */
  dateLimit?: moment.unitOfTime.DurationConstructor;
  /** Sets the value of the date limit. e.g. 1 (Default: 1) */
  dateLimitValue?: number;
  /** If enabled shows the mini version of the date picker. (Default: false) */
  isMini?: boolean;
  /** Custom width (in pixels) for the date input. (Default: 120) */
  width?: number;
  /** If the selected dates are bold. (Default: false) */
  isBold?: boolean;
  /**
   * Number of days the selected range needs to be. If set to 0 it will allow
   * any range to be selected. (Default: 0)
   */
  dayRange?: number;
}

export const isOutsideRangeCheck = (
  day: moment.Moment,
  dateLimitValue: number,
  dateLimit: moment.unitOfTime.DurationConstructor,
  isPastDatesOnly: boolean,
  isFutureDatesOnly: boolean,
): boolean => {
  return (
    !day.isAfter(
      moment()
        .subtract(dateLimitValue, dateLimit)
        .subtract(1, 'day'),
    ) ||
    !day.isBefore(moment().add(dateLimitValue, dateLimit)) ||
    (isPastDatesOnly && !isInclusivelyBeforeDay(day, moment())) ||
    (isFutureDatesOnly && !isInclusivelyAfterDay(day, moment()))
  );
};

type IDateRangePickerProps = IDatePickerProps & DateRangePickerShape & IFormItemProps;

export const DateRangePicker: FC<IDateRangePickerProps> = ({
  dateLimit = 'year',
  dateLimitValue = 1,
  disabled,
  hint,
  isBold,
  isFutureDatesOnly = false,
  isMini = false,
  isPastDatesOnly = false,
  label,
  dayRange = 0,
  spacingBelow,
  tooltipDescription,
  tooltipHeading,
  tooltipWidth,
  validationMessage,
  validationState,
  width,
  ...dateRangePickerProps
}) => {
  const isOutsideRange = useCallback(
    (day: moment.Moment) => isOutsideRangeCheck(day, dateLimitValue, dateLimit, isPastDatesOnly, isFutureDatesOnly),
    [dateLimitValue, dateLimit, isPastDatesOnly, isFutureDatesOnly],
  );

  const effectiveDisplayFormat = (dateRangePickerProps as any)?.displayFormat ?? 'DD/MM/YYYY';
  const effectiveDayAriaLabelFormat = (dateRangePickerProps as any)?.dayAriaLabelFormat ?? 'dddd, D MMMM YYYY';

  return (
    <StyledDatePicker isMini={isMini} width={width} isBold={isBold}>
      <FormItem
        disabled={disabled}
        for="startDate"
        hint={hint}
        isMini={isMini}
        label={label}
        spacingBelow={spacingBelow}
        tooltipDescription={tooltipDescription}
        tooltipHeading={tooltipHeading}
        tooltipWidth={tooltipWidth}
        validationMessage={validationMessage}
        validationState={validationState}
      >
        <RdDateRangePicker
          customInputIcon={isMini ? <Icon icon="angle-down" /> : null}
          customCloseIcon={<Icon icon="times" />}
          disabled={disabled}
          inputIconPosition="after"
          isOutsideRange={isOutsideRange}
          readOnly={true}
          showClearDates={!isMini}
          small={isMini}
          endDateOffset={dayRange >= 1 ? (day: moment.Moment) => day.add(dayRange - 1, 'days') : null}
          {...dateRangePickerProps}
          displayFormat={effectiveDisplayFormat}
          dayAriaLabelFormat={effectiveDayAriaLabelFormat}
        />
      </FormItem>
    </StyledDatePicker>
  );
};
